import React from "react";
import cx from "classnames";
import { Link } from "gatsby";
import { FaInstagram, FaPinterestP } from "react-icons/fa";
import { MenuItem } from "src/components/menu-item";
import { BlockContent } from "src/components/block-content";
import { Image } from "src/components/image";
import { Signup } from "src/components/signup";
import { useMediaQueries } from "src/hooks/useMediaQueries";

import {
  useStoreContext,
  useToggleCart,
  useToggleMenu,
  useToggleSubmenu,
  useSetActiveSubmenu,
} from "src/context/storeContext";
import { useFooterData } from "src/hooks/useFooterData";
import { getInternalLinkPath } from "src/utils/getInternalLinkPath";
import * as styles from "./footer.module.css";

const GeneralFooter = ({ data }) => {
  return (
    <div className={styles.general}>
      <div className={styles.row}>
        <div className={cx(styles.column)}>
          <div className={styles.columnInner}>
            <h4 className={cx("label-caps", styles.contentLabel)}>
              {data.leftColumnLabel}
            </h4>
            <BlockContent
              className={cx("headline")}
              copy={data.leftColumnCopy}
            />
            {data.leftColumnLinkTarget && (
              <Link
                className={styles.contentLink}
                to={getInternalLinkPath(data.leftColumnLinkTarget.link)}
              >
                <span className={cx("c-gold label-italic underline-on-hover")}>
                  {data.leftColumnLinkTitle}
                </span>
              </Link>
            )}
          </div>
        </div>
        <div className={cx(styles.column)}>
          <div className={styles.columnInner}>
            <h4 className={cx("label-caps", styles.contentLabel)}>
              {data.rightColumnLabel}
            </h4>
            <BlockContent
              className={cx("headline")}
              copy={data.rightColumnCopy}
            />
            {data.rightColumnLinkTarget && (
              <Link
                className={styles.contentLink}
                to={getInternalLinkPath(data.rightColumnLinkTarget?.link)}
              >
                <span className={cx("c-gold label-italic underline-on-hover")}>
                  {data.rightColumnLinkTitle}
                </span>
              </Link>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const SimpleFooter = ({ data }) => {
  return (
    <div className={styles.simple}>
      <h4 className={cx("label-caps", styles.contentLabel)}>{data.label}</h4>
      <BlockContent className={cx("headline")} copy={data.copy} />
      {data.linkTarget && data.linkTitle && (
        <Link
          className={styles.contentLink}
          to={getInternalLinkPath(data.linkTarget.link)}
        >
          <span className={cx("c-gold label-italic underline-on-hover")}>
            {data.linkTitle}
          </span>
        </Link>
      )}
    </div>
  );
};

const LandingPageFooter = ({ data }) => {
  return (
    <div className={styles.landingPage}>
      <div className={styles.landingPageIntro}>
        <h4 className={cx("label-caps", styles.contentLabel)}>
          {data.introLabel}
        </h4>
        <BlockContent className={cx("headline")} copy={data.introCopy} />
        {data.introLinkTarget && (
          <Link
            className={styles.contentLink}
            to={getInternalLinkPath(data.introLinkTarget?.link)}
          >
            <span className={cx("c-gold label-italic underline-on-hover")}>
              {data.introLinkTitle}
            </span>
          </Link>
        )}
      </div>
      <div className={styles.row}>
        <div className={cx(styles.column)}>
          <Image
            className={cx(styles.landingPageImage)}
            imageId={data.leftColumnImage.asset._id}
            alt={data.leftColumnImage.alt}
          />
          <BlockContent
            className={styles.landingPageCaption}
            copy={data.leftColumnCaption}
          />
        </div>
        <div className={cx(styles.column)}>
          <h4 className={cx("label-caps", styles.contentLabel)}>
            {data.rightColumnLabel}
          </h4>
          <BlockContent
            className={cx("headline")}
            copy={data.rightColumnCopy}
          />
          {data.rightColumnLinkTarget && (
            <Link
              className={styles.contentLink}
              to={getInternalLinkPath(data.rightColumnLinkTarget?.link)}
            >
              <span className={cx("c-gold label-italic underline-on-hover")}>
                {data.rightColumnLinkTitle}
              </span>
            </Link>
          )}
        </div>
      </div>
    </div>
  );
};

export const Footer = ({ location }) => {
  const {
    menuIsOpen,
    submenuIsOpen,
    cartIsOpen,
    activeSubmenu,
  } = useStoreContext();
  const toggleCart = useToggleCart();
  const toggleMenu = useToggleMenu();
  const toggleSubmenu = useToggleSubmenu();
  const setActiveSubmenu = useSetActiveSubmenu();
  const {
    footer: {
      general,
      landingPage,
      simple,
      menuSections,
      mobileMenu,
      subscribePrompt,
    },
    social: { instagram, pinterest },
  } = useFooterData();

  const { isMobile, isMobileOrTablet, isDesktop } = useMediaQueries();

  const handleClick = (event, item) => {
    menuIsOpen && toggleMenu();
    cartIsOpen && toggleCart();

    switch (item.type) {
      case "submenu":
        setActiveSubmenu(item.submenu);
        if (
          !submenuIsOpen ||
          (activeSubmenu && activeSubmenu.id === item.submenu.id)
        ) {
          toggleSubmenu();
        }
        break;
      case "internalLink":
        submenuIsOpen && toggleSubmenu();
        break;
      default:

      // console.log("default");
    }
  };

  return (
    <div className={styles.footer}>
      {isDesktop && (
        <>
          {/* TODO: pull page footer from CMS
          instead of relying on the logic below */}
          {location.pathname === "/" ? (
            <LandingPageFooter data={landingPage} />
          ) : location.pathname.includes("contact") ||
            location.pathname.includes("appointment") ||
            location.pathname.includes("about") ? (
            <SimpleFooter data={simple} />
          ) : (
            <GeneralFooter data={general} />
          )}
        </>
      )}
      {isMobileOrTablet && (
        <div className={styles.mobileFooter}>
          <SimpleFooter data={simple} />
        </div>
      )}
      <div className={cx(styles.menuSubscribe)}>
        <div className={cx("hide-max-medium", styles.menu)}>
          {menuSections &&
            menuSections.map((section) => {
              return (
                <div className={styles.menuSection} key={section._key}>
                  <h4 className={cx("label-caps")}>{section.title}</h4>
                  <ul>
                    {section.menu.items &&
                      section.menu.items.map((item) => (
                        <MenuItem
                          className={cx(styles.menuItem)}
                          item={item}
                          key={item._key}
                          clickHandler={(event) => handleClick(event, item)}
                        >
                          <span className="default c-gold underline-on-hover">
                            {item.title}
                          </span>
                        </MenuItem>
                      ))}
                  </ul>
                </div>
              );
            })}
        </div>
        <div className={styles.connect}>
          <h4 className={cx("label-caps")}>{subscribePrompt}</h4>
          <Signup />

          <ul className={styles.socialList}>
            {instagram && (
              <li>
                <a
                  className={cx("c-gold", styles.socialLink)}
                  href={instagram}
                  target="_blank"
                  rel="noreferrer nofollow"
                  aria-label="Instagram"
                >
                  <FaInstagram />
                </a>
              </li>
            )}
            {pinterest && (
              <li>
                <a
                  className={cx("c-gold", styles.socialLink)}
                  href={pinterest}
                  target="_blank"
                  rel="noreferrer nofollow"
                  aria-label="Pinterest"
                >
                  <FaPinterestP />
                </a>
              </li>
            )}
          </ul>
        </div>
        <div className={cx("hide-min-large", styles.mobileMenu)}>
          <ul>
            {mobileMenu.items &&
              mobileMenu.items.map((item) => (
                <MenuItem
                  className={cx(styles.menuItem)}
                  item={item}
                  key={item._key}
                  clickHandler={(event) => handleClick(event, item)}
                >
                  <span className="default c-gold underline-on-hover">
                    {item.title}
                  </span>
                </MenuItem>
              ))}
          </ul>
        </div>
      </div>
    </div>
  );
};
